// PakMazar service catalog — admin panel mirror.
//
// Kept in sync with:
//   mobile-app/lib/core/catalog/service_catalog.dart
//   functions/service-catalog.js
//
// functions/test/catalog-parity.test.js fails the build if the three copies
// drift apart, so the customer app, the backend and this panel can never
// disagree about a price or a proof requirement.

export const PACKAGE_TYPE = { ONE_TIME: 'one_time', ANNUAL: 'annual' };
export const PRICE_TYPE = { FIXED: 'fixed', FROM: 'from' };
export const VISIT_FREQUENCY = {
  MONTHLY: 'monthly',
  TWICE_MONTHLY: 'twice_monthly',
  WEEKLY: 'weekly',
};

export const BADGE_LABELS = {
  most_popular: 'MOST POPULAR',
  complete_care: 'COMPLETE CARE',
  best_value: 'BEST VALUE',
  maximum_care: 'MAXIMUM CARE',
};

export const SERVICE_PACKAGES = [
  {
    id: 'basic_care',
    packageType: PACKAGE_TYPE.ONE_TIME,
    price: 39,
    sortOrder: 10,
    badge: '',
    requiresPhotoReport: true,
    requiresVideoReport: false,
    customerPhotoReport: false,
    customerVideoReport: false,
    name: { en: 'Basic Care', az: 'Əsas Baxım', ru: 'Базовый уход' },
    includes: {
      en: [
        'Grass and weed removal',
        'Grave surface sweeping',
        'Light dirt cleaning',
        'Waste removal',
        'Surrounding-area tidy-up',
      ],
      az: [
        'Ot və alaq təmizliyi',
        'Məzar səthinin süpürülməsi',
        'Yüngül torpaq təmizliyi',
        'Zibilin yığılması',
        'Ətraf ərazinin səliqəyə salınması',
      ],
      ru: [
        'Удаление травы и сорняков',
        'Подметание поверхности могилы',
        'Лёгкая очистка от грязи',
        'Вывоз мусора',
        'Уборка прилегающей территории',
      ],
    },
  },
  {
    id: 'standard_care',
    packageType: PACKAGE_TYPE.ONE_TIME,
    price: 69,
    sortOrder: 20,
    badge: 'most_popular',
    requiresPhotoReport: true,
    requiresVideoReport: false,
    customerPhotoReport: true,
    customerVideoReport: false,
    name: { en: 'Standard Care', az: 'Standart Baxım', ru: 'Стандартный уход' },
    includes: {
      en: [
        'Everything in Basic',
        'Monument/headstone cleaning',
        'Detailed grave surface cleaning',
        'Border and surrounding-area cleaning',
        'Light flower-area tidying',
        'Before & after photo report',
      ],
      az: [
        'Əsas paketin hamısı',
        'Abidənin təmizlənməsi',
        'Məzar səthinin detallı təmizliyi',
        'Haşiyə və ətraf ərazinin təmizliyi',
        'Gül sahəsinin yüngül səliqəsi',
        'Əvvəl və sonra foto hesabat',
      ],
      ru: [
        'Всё из «Базового ухода»',
        'Очистка памятника',
        'Детальная очистка поверхности могилы',
        'Очистка бордюра и прилегающей территории',
        'Лёгкая уборка цветочной зоны',
        'Фотоотчёт «до и после»',
      ],
    },
  },
  {
    id: 'premium_care',
    packageType: PACKAGE_TYPE.ONE_TIME,
    price: 109,
    sortOrder: 30,
    badge: 'complete_care',
    requiresPhotoReport: true,
    requiresVideoReport: true,
    customerPhotoReport: true,
    customerVideoReport: true,
    name: { en: 'Premium Care', az: 'Premium Baxım', ru: 'Премиум уход' },
    includes: {
      en: [
        'Everything in Standard',
        'Deep monument/headstone cleaning',
        'Detailed border cleaning',
        'More intensive weed removal',
        'Final presentation/tidying',
        'Before & after photo report',
        'Completion video report',
      ],
      az: [
        'Standart paketin hamısı',
        'Abidənin dərin təmizliyi',
        'Haşiyənin detallı təmizliyi',
        'Daha intensiv alaq təmizliyi',
        'Yekun səliqə və təqdimat',
        'Əvvəl və sonra foto hesabat',
        'Tamamlanma video hesabatı',
      ],
      ru: [
        'Всё из «Стандартного ухода»',
        'Глубокая очистка памятника',
        'Детальная очистка бордюра',
        'Более интенсивное удаление сорняков',
        'Финальная уборка и оформление',
        'Фотоотчёт «до и после»',
        'Видеоотчёт о завершении',
      ],
    },
  },
  {
    id: 'annual_monthly',
    packageType: PACKAGE_TYPE.ANNUAL,
    price: 399,
    sortOrder: 40,
    badge: '',
    requiresPhotoReport: true,
    requiresVideoReport: false,
    customerPhotoReport: true,
    customerVideoReport: false,
    visitFrequency: VISIT_FREQUENCY.MONTHLY,
    totalVisits: 12,
    monthlyVideoVisit: false,
    name: { en: 'Monthly Care', az: 'Aylıq Baxım', ru: 'Ежемесячный уход' },
    includes: {
      en: [
        'Grass and weed removal',
        'Grave surface cleaning',
        'Monument wipe-down',
        'Waste removal',
        'Surrounding-area tidy-up',
        'Photo report after each visit',
      ],
      az: [
        'Ot və alaq təmizliyi',
        'Məzar səthinin təmizliyi',
        'Abidənin silinməsi',
        'Zibilin yığılması',
        'Ətraf ərazinin səliqəsi',
        'Hər baxışdan sonra foto hesabat',
      ],
      ru: [
        'Удаление травы и сорняков',
        'Очистка поверхности могилы',
        'Протирка памятника',
        'Вывоз мусора',
        'Уборка прилегающей территории',
        'Фотоотчёт после каждого визита',
      ],
    },
  },
  {
    id: 'annual_twice_monthly',
    packageType: PACKAGE_TYPE.ANNUAL,
    price: 699,
    sortOrder: 50,
    badge: 'best_value',
    requiresPhotoReport: true,
    requiresVideoReport: false,
    customerPhotoReport: true,
    customerVideoReport: false,
    visitFrequency: VISIT_FREQUENCY.TWICE_MONTHLY,
    totalVisits: 24,
    monthlyVideoVisit: false,
    name: {
      en: 'Twice-Monthly Care',
      az: 'İkihəftəlik Baxım',
      ru: 'Уход дважды в месяц',
    },
    includes: {
      en: [
        'Complete routine care',
        'Weed control',
        'Grave cleaning',
        'Monument cleaning',
        'Surrounding area tidy',
        'Waste removal',
        'Photo report',
      ],
      az: [
        'Tam rutin baxım',
        'Alaq nəzarəti',
        'Məzarın təmizlənməsi',
        'Abidənin təmizlənməsi',
        'Ətraf ərazinin səliqəsi',
        'Zibilin yığılması',
        'Foto hesabat',
      ],
      ru: [
        'Полный регулярный уход',
        'Контроль сорняков',
        'Очистка могилы',
        'Очистка памятника',
        'Уборка прилегающей территории',
        'Вывоз мусора',
        'Фотоотчёт',
      ],
    },
  },
  {
    id: 'annual_weekly',
    packageType: PACKAGE_TYPE.ANNUAL,
    price: 1199,
    sortOrder: 60,
    badge: 'maximum_care',
    requiresPhotoReport: true,
    requiresVideoReport: false,
    customerPhotoReport: true,
    customerVideoReport: false,
    visitFrequency: VISIT_FREQUENCY.WEEKLY,
    totalVisits: 52,
    // One video report per calendar month, on the first visit of that month.
    monthlyVideoVisit: true,
    name: { en: 'Weekly Care', az: 'Həftəlik Baxım', ru: 'Еженедельный уход' },
    includes: {
      en: [
        'Routine grave maintenance',
        'Grass/weed control',
        'Grave cleaning',
        'Monument wipe-down',
        'Waste removal',
        'Surrounding maintenance',
        'Photo report after each visit',
        'One video report per month',
      ],
      az: [
        'Rutin məzar baxımı',
        'Ot və alaq nəzarəti',
        'Məzarın təmizlənməsi',
        'Abidənin silinməsi',
        'Zibilin yığılması',
        'Ətraf ərazinin baxımı',
        'Hər baxışdan sonra foto hesabat',
        'Ayda bir video hesabat',
      ],
      ru: [
        'Регулярное обслуживание могилы',
        'Контроль травы и сорняков',
        'Очистка могилы',
        'Протирка памятника',
        'Вывоз мусора',
        'Обслуживание прилегающей территории',
        'Фотоотчёт после каждого визита',
        'Один видеоотчёт в месяц',
      ],
    },
  },
];

export const FLOWER_ADD_ONS = [
  { id: 'no_flowers', price: 0, priceType: PRICE_TYPE.FIXED,
    name: { en: 'No flowers', az: 'Gül yoxdur', ru: 'Без цветов' } },
  { id: 'small_memorial_bouquet', price: 45, priceType: PRICE_TYPE.FIXED,
    name: { en: 'Small memorial bouquet', az: 'Kiçik anım buketi',
      ru: 'Небольшой памятный букет' } },
  { id: 'standard_fresh_arrangement', price: 70, priceType: PRICE_TYPE.FIXED,
    name: { en: 'Standard fresh arrangement',
      az: 'Standart təzə kompozisiya', ru: 'Стандартная свежая композиция' } },
  { id: 'premium_memorial_arrangement', price: 110, priceType: PRICE_TYPE.FIXED,
    name: { en: 'Premium memorial arrangement',
      az: 'Premium anım kompozisiyası', ru: 'Премиум памятная композиция' } },
  { id: 'large_memorial_arrangement', price: 160, priceType: PRICE_TYPE.FROM,
    name: { en: 'Large memorial arrangement',
      az: 'Böyük anım kompozisiyası', ru: 'Большая памятная композиция' } },
];

export const SERVICE_ADD_ONS = [
  { id: 'extra_photo_set', price: 10, priceType: PRICE_TYPE.FIXED,
    addsPhotoRequirement: true, addsVideoRequirement: false,
    name: { en: 'Extra photo set', az: 'Əlavə foto dəsti',
      ru: 'Дополнительный набор фото' } },
  { id: 'extra_video_report', price: 15, priceType: PRICE_TYPE.FIXED,
    addsPhotoRequirement: false, addsVideoRequirement: true,
    name: { en: 'Extra video report', az: 'Əlavə video hesabat',
      ru: 'Дополнительный видеоотчёт' } },
  { id: 'heavy_overgrowth_cleaning', price: 30, priceType: PRICE_TYPE.FROM,
    addsPhotoRequirement: false, addsVideoRequirement: false,
    name: { en: 'Heavy overgrowth cleaning',
      az: 'Güclü otlaşmanın təmizlənməsi', ru: 'Очистка сильного зарастания' } },
  { id: 'deep_stone_cleaning', price: 35, priceType: PRICE_TYPE.FROM,
    addsPhotoRequirement: false, addsVideoRequirement: false,
    name: { en: 'Deep stone cleaning', az: 'Daşın dərin təmizliyi',
      ru: 'Глубокая очистка камня' } },
  { id: 'special_date_visit', price: 25, priceType: PRICE_TYPE.FIXED,
    addsPhotoRequirement: false, addsVideoRequirement: false,
    name: { en: 'Special-date visit', az: 'Xüsusi tarix ziyarəti',
      ru: 'Визит в особую дату' } },
  { id: 'candle_memorial_arrangement', price: 15, priceType: PRICE_TYPE.FROM,
    addsPhotoRequirement: false, addsVideoRequirement: false,
    name: { en: 'Candle / memorial arrangement',
      az: 'Şam / anım kompozisiyası', ru: 'Свеча / памятная композиция' } },
];

export const packageById = (id) =>
  SERVICE_PACKAGES.find((p) => p.id === id) || null;
export const addOnById = (id) =>
  SERVICE_ADD_ONS.find((a) => a.id === id) || null;
export const flowerById = (id) =>
  FLOWER_ADD_ONS.find((f) => f.id === id) || null;

export const isQuoted = (item) =>
  Boolean(item) && item.priceType === PRICE_TYPE.FROM;

/** Localized helper used by the panel's language switcher. */
export function localized(entry, lang, field = 'name') {
  if (!entry) return '';
  const value = entry[field];
  if (!value) return '';
  return value[lang] || value.en || '';
}

/**
 * The Firestore document body for a catalog package.
 *
 * `packages` remains the store of record so Admin can see the live catalog,
 * but the definitions above are the authority. Historical orders are untouched
 * by this: every order carries its own frozen `packageSnapshot`.
 */
export function packageDocument(pkg) {
  return {
    catalogId: pkg.id,
    name: pkg.name.en,
    nameEn: pkg.name.en,
    nameAz: pkg.name.az,
    nameRu: pkg.name.ru,
    localizedName: pkg.name,
    price: pkg.price,
    priceType: PRICE_TYPE.FIXED,
    packageType: pkg.packageType,
    includes: pkg.includes.en,
    localizedIncludes: pkg.includes,
    featuresEn: pkg.includes.en,
    requiresPhotoReport: pkg.requiresPhotoReport,
    requiresVideoReport: pkg.requiresVideoReport,
    // Legacy field names, so an older mobile build keeps reading correctly.
    supportsPhotoReport: pkg.requiresPhotoReport,
    supportsVideoReport: pkg.requiresVideoReport,
    badge: pkg.badge || '',
    visitFrequency: pkg.visitFrequency || '',
    totalVisits: pkg.totalVisits || 0,
    monthlyVideoVisit: Boolean(pkg.monthlyVideoVisit),
    sortOrder: pkg.sortOrder,
    isActive: true,
    active: true,
  };
}

/**
 * Works out what a catalog sync would change, without touching Firestore.
 *
 * Returns the packages to upsert and the stale documents to deactivate. A
 * legacy package is only ever DEACTIVATED, never deleted, so historical orders
 * that reference it keep resolving.
 *
 * @param {Array<{id: string, catalogId?: string, isActive?: boolean,
 *                active?: boolean}>} existingDocs Current `packages` docs.
 * @return {{upserts: Array, deactivate: Array}}
 */
export function planCatalogSync(existingDocs) {
  const docs = Array.isArray(existingDocs) ? existingDocs : [];
  const byCatalogId = new Map();
  for (const doc of docs) {
    const key = doc.catalogId || doc.id;
    if (packageById(key)) byCatalogId.set(key, doc);
  }

  const upserts = SERVICE_PACKAGES.map((pkg) => ({
    catalogId: pkg.id,
    docId: byCatalogId.has(pkg.id) ? byCatalogId.get(pkg.id).id : null,
    data: packageDocument(pkg),
  }));

  const deactivate = docs
    .filter((doc) => !packageById(doc.catalogId || doc.id))
    .filter((doc) => (doc.isActive ?? doc.active ?? true) !== false)
    .map((doc) => doc.id);

  return { upserts, deactivate };
}

/** Resolve the live badge while preserving the canonical active default. */
export function isPackageActive(catalogPackage, remotePackage) {
  if (remotePackage) {
    return remotePackage.isActive ?? remotePackage.active ??
      catalogPackage?.isActive ?? true;
  }
  return catalogPackage?.isActive ?? true;
}
